import { useEffect, useState } from "react";
import { Brain, Loader2, Check, X, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Beklemede" },
  { key: "approved", label: "Onaylı" },
  { key: "rejected", label: "Reddedilen" },
];

export default function AdminConcepts() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState({ term: "", synonyms: "" });

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/concepts", { params: { status } });
      setItems(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const act = async (fn) => { try { await fn(); load(tab); } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İşlem başarısız"); } };
  const approve = (c) => act(() => api.post(`/admin/concepts/${c.id}/approve`).then(() => toast.success(`"${c.term}" onaylandı`)));
  const reject = (c) => act(() => api.post(`/admin/concepts/${c.id}/reject`).then(() => toast.success(`"${c.term}" reddedildi`)));
  const del = (c) => act(() => api.delete(`/admin/concepts/${c.id}`).then(() => toast.success("Silindi")));

  const startEdit = (c) => { setEditId(c.id); setEditVal({ term: c.term, synonyms: (c.synonyms || []).join(", ") }); };
  const saveEdit = (c) => act(() => api.put(`/admin/concepts/${c.id}`, {
    term: editVal.term.trim(),
    synonyms: editVal.synonyms.split(",").map((s) => s.trim()).filter(Boolean),
  }).then(() => { setEditId(null); toast.success("Güncellendi"); }));

  return (
    <div data-testid="admin-concepts-page">
      <div className="flex items-center gap-2 mb-1">
        <Brain className="text-gold" size={22} />
        <h1 className="font-display text-3xl text-foreground">AI Kavramlar</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        AI'ın ürünlerden öğrendiği yeni kuyumculuk kavramlarını onaylayın; onaylananlar arama motorunda kullanılır.
      </p>

      <div className="flex gap-2 mb-5">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`concept-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 h-9 rounded-full text-sm border transition-colors ${tab === t.key ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={26} /></div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm py-12 text-center border border-dashed border-border rounded-lg">Bu listede kavram yok.</p>
      ) : (
        <div className="space-y-2 max-w-3xl" data-testid="concept-list">
          {items.map((c) => (
            <div key={c.id} data-testid={`concept-item-${c.term}`} className="bg-surface border border-border rounded-md px-4 py-3">
              {editId === c.id ? (
                <div className="space-y-2">
                  <Input value={editVal.term} onChange={(e) => setEditVal((v) => ({ ...v, term: e.target.value }))} placeholder="Kavram" className="bg-obsidian border-border h-9" data-testid={`concept-edit-term-${c.term}`} />
                  <Input value={editVal.synonyms} onChange={(e) => setEditVal((v) => ({ ...v, synonyms: e.target.value }))} placeholder="Eş anlamlılar (virgülle)" className="bg-obsidian border-border h-9" data-testid={`concept-edit-syn-${c.term}`} />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(c)} className="px-3 h-8 bg-gold text-obsidian rounded-md text-sm font-medium">Kaydet</button>
                    <button onClick={() => setEditId(null)} className="px-3 h-8 border border-border rounded-md text-sm text-muted-foreground">Vazgeç</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-foreground font-medium">{c.term}</span>
                      {c.source && <span className="text-[10px] text-muted-foreground border border-border rounded-full px-2 py-0.5">{c.source}</span>}
                      {c.seen > 1 && <span className="text-[10px] text-gold">×{c.seen}</span>}
                    </div>
                    {(c.synonyms || []).length > 0 && <p className="text-xs text-muted-foreground mt-1 truncate">{(c.synonyms || []).join(", ")}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => startEdit(c)} data-testid={`concept-edit-${c.term}`} className="px-2 h-8 border border-border rounded-md text-xs text-muted-foreground hover:text-gold transition-colors">Düzenle</button>
                    {c.status !== "approved" && <button onClick={() => approve(c)} data-testid={`concept-approve-${c.term}`} className="w-8 h-8 rounded-md border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 flex items-center justify-center"><Check size={15} /></button>}
                    {c.status !== "rejected" && <button onClick={() => reject(c)} data-testid={`concept-reject-${c.term}`} className="w-8 h-8 rounded-md border border-border text-muted-foreground hover:text-amber-400 flex items-center justify-center"><X size={15} /></button>}
                    <button onClick={() => del(c)} data-testid={`concept-delete-${c.term}`} className="w-8 h-8 rounded-md border border-border text-muted-foreground hover:text-red-400 flex items-center justify-center"><Trash2 size={14} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
